/**
 * Per-market collection sort via Merch Tool API, with metafield fallback.
 * Reorders product cards as they load (including load-more pagination).
 * AU uses native Shopify collection order — no JS intervention.
 */

const MERCH_API_BASE = 'https://merchtool.manus.space/api/collection-order';
const MERCH_API_KEY = 'cok_e696feeea3f7945864e70e19d373240a6e026d15a263ba7fe57dca997b8cadd9';
const SUPPORTED_MARKETS = new Set(['US', 'CA', 'NZ', 'GB', 'EU']);
const FETCH_TIMEOUT_MS = 3000;

/**
 * @param {string|number} collectionId
 * @param {string} market
 * @returns {Promise<number[]>}
 */
async function fetchMerchSortOrder(collectionId, market) {
  if (market === 'AU' || !SUPPORTED_MARKETS.has(market)) return [];

  try {
    const res = await fetch(`${MERCH_API_BASE}/${collectionId}/${market}`, {
      headers: {
        'X-API-Key': MERCH_API_KEY,
      },
      signal: AbortSignal.timeout(FETCH_TIMEOUT_MS),
    });

    if (!res.ok) throw new Error(`HTTP ${res.status}`);

    const data = await res.json();
    return Array.isArray(data.productIds) ? data.productIds.map(Number) : [];
  } catch (err) {
    console.warn('[MerchTool] Sort order endpoint unavailable, falling back to metafield:', err);
    return [];
  }
}

/**
 * @param {HTMLElement[]} products
 * @param {number[]} productIds
 * @returns {HTMLElement[]}
 */
function applyMerchOrder(products, productIds) {
  if (!productIds || productIds.length === 0) return products;

  const positionMap = new Map(productIds.map((id, index) => [Number(id), index]));

  return [...products].sort((a, b) => {
    const idA = Number(a.dataset.productId);
    const idB = Number(b.dataset.productId);
    const posA = positionMap.has(idA) ? positionMap.get(idA) : Infinity;
    const posB = positionMap.has(idB) ? positionMap.get(idB) : Infinity;
    return posA - posB;
  });
}

/**
 * @param {HTMLElement} root
 * @returns {number[]}
 */
function getFallbackOrderFromMetafield(root) {
  const raw = root.getAttribute('data-market-sort-fallback');
  if (!raw) return [];

  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.map(Number).filter((id) => !Number.isNaN(id));
  } catch {
    return [];
  }
}

/**
 * Skip merch order when the shopper chose a non-manual sort.
 * @returns {boolean}
 */
function shouldApplyMerchSort() {
  const url = new URL(window.location.href);
  const sortBy = url.searchParams.get('sort_by');
  if (!sortBy || sortBy === 'manual') return true;
  return false;
}

export default class CollectionMarketSort {
  /** @type {HTMLElement} */
  #root;

  /** @type {number[]} */
  #productIds = [];

  /** @type {boolean} */
  #sorting = false;

  /** @type {MutationObserver | null} */
  #observer = null;

  /** @type {number | null} */
  #scheduledFrame = null;

  /**
   * @param {HTMLElement} root
   */
  constructor(root) {
    this.#root = root;
  }

  async init() {
    const collectionId = this.#root.getAttribute('data-collection-id');
    const market = (this.#root.getAttribute('data-market') || '').toUpperCase();

    if (!collectionId || !market || market === 'AU') return;

    const fallbackOrder = getFallbackOrderFromMetafield(this.#root);
    const apiOrder = await fetchMerchSortOrder(collectionId, market);
    this.#productIds = apiOrder.length > 0 ? apiOrder : fallbackOrder;

    if (this.#productIds.length === 0) return;

    this.#sortGrid();
    this.#observeGrid();

    document.addEventListener('filter:update', this.#handleFilterUpdate);
  }

  destroy() {
    this.#observer?.disconnect();
    this.#observer = null;
    if (this.#scheduledFrame != null) {
      cancelAnimationFrame(this.#scheduledFrame);
      this.#scheduledFrame = null;
    }
    document.removeEventListener('filter:update', this.#handleFilterUpdate);
  }

  #handleFilterUpdate = () => {
    // FilterUpdate fires before section morph; wait for the grid to update, then re-bind.
    const pending = new MutationObserver(() => {
      if (!this.#getGrid()) return;
      pending.disconnect();
      this.#observer?.disconnect();
      this.#observeGrid();
      this.#sortGrid();
    });

    pending.observe(this.#root, { childList: true, subtree: true });
    setTimeout(() => pending.disconnect(), 3000);
  };

  #observeGrid() {
    const grid = this.#getGrid();
    if (!grid) return;

    this.#observer = new MutationObserver(() => {
      if (this.#sorting) return;
      this.#scheduleSort();
    });

    this.#observer.observe(grid, { childList: true });
  }

  #scheduleSort() {
    if (this.#scheduledFrame != null) return;
    this.#scheduledFrame = requestAnimationFrame(() => {
      this.#scheduledFrame = null;
      this.#sortGrid();
    });
  }

  /**
   * @returns {HTMLElement | null}
   */
  #getGrid() {
    return this.#root.querySelector('[ref="grid"]');
  }

  #sortGrid() {
    if (!shouldApplyMerchSort()) return;
    if (!this.#productIds.length) return;

    const grid = this.#getGrid();
    if (!grid) return;

    const cards = /** @type {HTMLElement[]} */ (
      [...grid.querySelectorAll(':scope > [data-product-id][ref="cards[]"]')]
    );
    if (cards.length < 2) return;

    const sorted = applyMerchOrder(cards, this.#productIds);
    let orderChanged = false;
    for (let i = 0; i < sorted.length; i++) {
      if (sorted[i] !== cards[i]) {
        orderChanged = true;
        break;
      }
    }
    if (!orderChanged) return;

    // Preserve viewport position around the first visible card.
    let anchor = null;
    let anchorTop = 0;
    for (const card of cards) {
      const rect = card.getBoundingClientRect();
      if (rect.bottom > 0 && rect.top < window.innerHeight) {
        anchor = card;
        anchorTop = rect.top;
        break;
      }
    }

    this.#sorting = true;
    sorted.forEach((card, index) => {
      card.className = card.className.replace(/product-grid__item--\d+/, `product-grid__item--${index}`);
      grid.appendChild(card);
    });
    this.#sorting = false;

    if (anchor) {
      const newTop = anchor.getBoundingClientRect().top;
      const diff = newTop - anchorTop;
      if (Math.abs(diff) > 1) {
        window.scrollBy(0, diff);
      }
    }
  }
}

/**
 * @param {HTMLElement} [root]
 */
export async function initCollectionMarketSort(root = document.querySelector('results-list[data-collection-id][data-market]')) {
  if (!(root instanceof HTMLElement)) return null;

  const instance = new CollectionMarketSort(root);
  await instance.init();
  return instance;
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    initCollectionMarketSort();
  }, { once: true });
} else {
  initCollectionMarketSort();
}
